import React, { useCallback, useMemo } from "react";

import { useApiUrl, useDataProvider, useNotification } from "@refinedev/core";
import _ from "lodash";
import { CheckCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GenericTicketAssignment } from "@/pages/orders/components/providers/generic";
import { OrderBasedTicketAssignment } from "@/pages/orders/components/providers/order-based";
import { TicketCard } from "@/pages/orders/components/ticket";

export function TicketsDialog({ order }: { order: any }) {
  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();
  const { open } = useNotification();

  const [submitLoading, setSubmitLoading] = React.useState(false);

  const [tickets, setTickets] = React.useState<any[]>(order?.tickets ?? []);

  const onSubmit = useCallback(async () => {
    if (_.isEmpty(tickets)) {
      open?.({
        message: "Tickets are empty",
        description: "Select correct tickets",
        type: "error",
      });
    }
    setSubmitLoading(true);
    try {
      const response = await custom?.({
        url: `${apiUrl}/orders/assign-tickets`,
        method: "post",
        payload: {
          id: order.id,
          tickets,
        },
      });

      if (!response) {
        open?.({
          message: `Response to provider failed for provider ${order.ticketing.provider}`,
          type: "error",
        });
        return;
      }

      open?.({
        message: "Tickets assigned successfully",
        description: "The tickets have been assigned to the order.",
        type: "success",
      });
      window.location.reload();
    } catch (ex) {
    } finally {
      setSubmitLoading(false);
    }
  }, [tickets]);

  const AlertComponent = useMemo(() => {
    const requestedProducts: { name: string; quantity: number }[] =
      order.params.products;

    if (tickets.length === 0) {
      return <></>;
    }

    const ticketsByName = Object.groupBy(tickets, (t) => t.name);
    const ticketsAsProducts: { name: string; quantity: number }[] = [];
    Object.keys(ticketsByName).forEach((k, v) => {
      ticketsAsProducts.push({
        name: k,
        quantity: Array.isArray(ticketsByName[k]) ? ticketsByName[k].length : 0,
      });
    });

    let orderStatus: "correct" | "less" | "more" | undefined;
    if (ticketsAsProducts.length === requestedProducts.length) {
      orderStatus = "correct";
    }

    ticketsAsProducts.every((t) => {
      const desiredQuantity = requestedProducts.find(
        ({ name }) => t.name === name,
      )?.quantity;
      if (!desiredQuantity || desiredQuantity < t.quantity) {
        orderStatus = "more";
        return false;
      }
      if (desiredQuantity > t.quantity) {
        orderStatus = "less";
        return false;
      }
      return true;
    });

    switch (orderStatus) {
      case "correct":
        return (
          <Alert variant={"default"} className="w-full self-center">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>You rock!</AlertTitle>
            <AlertDescription>
              Manually acquired tickets are exactly what the user requested in
              this order!
            </AlertDescription>
          </Alert>
        );
      case "less":
        return (
          <Alert variant={"destructive"} className="w-full self-center">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>Oops</AlertTitle>
            <AlertDescription>
              <div className="flex flex-col">
                <div>
                  You bought less tickets than the user requested, go buy more
                  ❤️
                </div>
                {requestedProducts.map((p) => (
                  <div>{`${p.quantity}x${p.name}`}</div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        );
      case "more":
        return (
          <Alert variant={"destructive"} className="w-full self-center">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>Heads up!</AlertTitle>
            <AlertDescription>
              You bought more tickets than the user requested
            </AlertDescription>
            <div className="flex flex-col">
              {requestedProducts.map((p, idx) => (
                <div key={idx}>{`${p.quantity} x ${p.name}`}</div>
              ))}
            </div>
          </Alert>
        );
    }
  }, [tickets, order]);

  const AssignmentForm = useMemo(() => {
    switch (order.ticketing.provider) {
      case "cometogether.live":
      case "weeztix.com":
        return (
          <OrderBasedTicketAssignment order={order} setTickets={setTickets} />
        );
      default:
        return (
          <GenericTicketAssignment order={order} setTickets={setTickets} />
        );
    }
  }, [order.provider]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="default">
          {order.botStatus === "PurchaseError"
            ? "Assign Tickets"
            : "View Tickets"}
          {/* {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? "Loading..." : "Assign Tickets"} */}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-svh overflow-auto">
        <DialogHeader>
          <DialogTitle>
            {order.botStatus === "PurchaseError"
              ? "Assign tickets manually to the order"
              : "View tickets attached to the order"}
          </DialogTitle>
          <DialogDescription>
            {order.botStatus === "PurchaseError"
              ? "Manual ticket attribution means that you will go buy the tickets for the user due to bot failure"
              : "Don't steal any ticket you fox"}
          </DialogDescription>
        </DialogHeader>
        {order.botStatus === "PurchaseError" && (
          <Tabs defaultValue="default" className="w-[400px]">
            <TabsList>
              <TabsTrigger value="default">Default</TabsTrigger>
              <TabsTrigger value="manual">Manual</TabsTrigger>
            </TabsList>
            <TabsContent value="default">{AssignmentForm}</TabsContent>
            <TabsContent value="manual">
              <GenericTicketAssignment order={order} setTickets={setTickets} />
            </TabsContent>
          </Tabs>
        )}
        <ScrollArea className="max-h-96">
          <div className="flex flex-col gap-2">
            {tickets.map((ticket, idx) => (
              <TicketCard
                key={idx}
                idx={idx}
                ticket={ticket}
                order={order}
                onRemove={(idx: number) =>
                  setTickets((prev) => prev.filter((_, _idx) => idx !== _idx))
                }
              />
            ))}
          </div>
        </ScrollArea>
        <DialogFooter>
          {order.botStatus === "PurchaseError" && (
            <div className="flex flex-col w-full gap-2">
              {AlertComponent}
              <Button
                className="self-end"
                disabled={tickets.length === 0 || submitLoading}
                onClick={onSubmit}
              >
                {submitLoading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {submitLoading ? "Loading..." : "Submit"}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
